import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Avatar,
  Tooltip,
  Drawer,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from "@mui/x-data-grid";
import { Link } from "react-router-dom";
import * as MuiIcons from "@mui/icons-material";
import StarIcon from "@mui/icons-material/Star";
import { groupNameFor } from "./Utils/neighborhood.utils";

/* ------------------------------- Helpers ------------------------------- */
const buildAssignees = (order, getAssignedUserName) => {
  if (Array.isArray(order?.assignees_details) && order.assignees_details.length) {
    return order.assignees_details.map((a) => ({
      id: a.user_id,
      name: `${a.name || ""} ${a.lastname || ""}`.trim() || `#${a.user_id}`,
      isPrimary: !!a.is_primary,
    }));
  }
  if (Array.isArray(order?.assignees) && order.assignees.length) {
    return order.assignees.map((id, idx) => ({
      id,
      name: getAssignedUserName ? (getAssignedUserName(id) || `#${id}`) : `#${id}`,
      isPrimary: idx === 0,
    }));
  }
  if (order?.assign_to != null) {
    const id = order.assign_to;
    return [
      {
        id,
        name: getAssignedUserName ? (getAssignedUserName(id) || `#${id}`) : `#${id}`,
        isPrimary: true,
      },
    ];
  }
  return [];
};

const initialsOf = (full) =>
  (full || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || "")
    .join("") || "?";

/* ------------------------------- Toolbar ------------------------------- */
const TableToolbar = () => (
  <GridToolbarContainer>
    <GridToolbarColumnsButton />
    <GridToolbarFilterButton />
    <GridToolbarDensitySelector />
    <GridToolbarExport />
  </GridToolbarContainer>
);

/* ------------------------------ Main Table ------------------------------ */
const WorkOrderTable = ({ rows = [], getAssignedUserName, getTypeName, getStatusInfo }) => {
  const [selectedRow, setSelectedRow] = useState(null);

  /* ✅ Normalize rows before rendering */
  const normalizedRows = rows.map((o) => {
    const openedByLabel =
      o.opened_by_name ||
      (o.opened_by_user_id != null && getAssignedUserName
        ? getAssignedUserName(o.opened_by_user_id)
        : null) ||
      (o.opened_by_user_id != null ? `#${o.opened_by_user_id}` : null) ||
      "Unknown";

    return {
      ...o,
      full_address: o.full_address || o.address || "N/A",
      ClubMembershipTypeCode: o.ClubMembershipTypeCode || o.club_membership_type_code || "",
      fullname:
        (o.fullname ||
          `${o.FirstName || ""} ${o.LastName || ""}`)
          .replace(/\s+/g, " ")
          .trim() || "N/A",
      description: o.description || "N/A",
      details: o.details || "", // ✅ add details field
      opened_by_display: openedByLabel, // ✅ use display name for export
      created_at: o.created_at
        ? new Date(o.created_at).toISOString().slice(0, 10)
        : "",
      resolved_at: o.resolved_at
        ? new Date(o.resolved_at).toISOString().slice(0, 10)
        : "",
    };
  });

  /* ---------------------------- Columns ---------------------------- */
  const columns = [
    {
      field: "order_id",
      headerName: "Work Order ID",
      width: 140,
      renderCell: (params) =>
        params.value ? (
          <Button
            component={Link}
            to={`${params.value}`}
            variant="text"
            size="small"
            sx={{ textTransform: "none" }}
          >
            WO-{params.value}
          </Button>
        ) : (
          "N/A"
        ),
    },
    { field: "property_id", headerName: "Property ID", width: 150 },
    { field: "fullname", headerName: "Full Name", width: 160 },
    { field: "full_address", headerName: "Address", flex: 1, minWidth: 200 },
    {
      field: "ClubMembershipTypeCode",
      headerName: "Membership Type",
      width: 180,
      renderCell: (params) => (params.value ? groupNameFor(params.value) : "N/A"),
    },
    { field: "priority", headerName: "Priority", width: 130 },

    // ✅ “Opened By” now exports the name, not ID
    {
      field: "opened_by_display",
      headerName: "Opened By",
      width: 200,
      renderCell: (params) => {
        const label = params.value;
        return (
          <Chip
            label={label}
            size="small"
            variant="outlined"
            avatar={
              <Avatar
                sx={{
                  width: 22,
                  height: 22,
                  fontSize: 11,
                  bgcolor: "grey.300",
                }}
              >
                {initialsOf(label)}
              </Avatar>
            }
          />
        );
      },
    },

    {
      field: "assign_to",
      headerName: "Assigned To",
      width: 280,
      renderCell: (params) => {
        const assignees = buildAssignees(params.row, getAssignedUserName);
        if (!assignees.length)
          return <Chip label="Not Assigned" size="small" color="default" />;
        return (
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {assignees.map((a) => {
              const label = a.name || `#${a.id}`;
              return (
                <Tooltip key={a.id} title={a.isPrimary ? `${label} (Primary)` : label}>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={label}
                    avatar={
                      <Avatar
                        sx={{
                          width: 22,
                          height: 22,
                          fontSize: 11,
                          bgcolor: a.isPrimary ? "primary.main" : "grey.300",
                          color: a.isPrimary ? "primary.contrastText" : "text.primary",
                        }}
                      >
                        {initialsOf(label)}
                      </Avatar>
                    }
                    icon={a.isPrimary ? <StarIcon fontSize="small" /> : undefined}
                    sx={{ "& .MuiChip-icon": { ml: 0.25 } }}
                  />
                </Tooltip>
              );
            })}
          </Box>
        );
      },
    },

    { field: "workorder_type_id", headerName: "Type", width: 140, renderCell: (params) => getTypeName?.(params.value) || "N/A" },
    { field: "subtype_name", headerName: "Subtype", width: 150 },
    { field: "location_name", headerName: "Location", width: 150 },
    { field: "created_at", headerName: "Created Date", width: 140 },
    { field: "resolved_at", headerName: "Resolved Date", width: 140 },

    // ✅ Added “Details” column
    {
      field: "details",
      headerName: "Details",
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <div
          style={{
            whiteSpace: "normal",
            wordBreak: "break-word",
            lineHeight: 1.3,
          }}
        >
          {params?.value || "N/A"}
        </div>
      ),
    },

    { field: "description", headerName: "Description", flex: 1, minWidth: 220 },

    {
      field: "status",
      headerName: "Status",
      width: 180,
      renderCell: (params) => {
        const statusInfo = getStatusInfo?.(params.row?.status) || {};
        const IconComponent = statusInfo.icon && MuiIcons[statusInfo.icon];
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {IconComponent && (
              <IconComponent fontSize="small" color={statusInfo.color || "action"} />
            )}
            <Chip
              label={statusInfo.statusname || "Unknown"}
              color={statusInfo.color || "default"}
            />
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 160,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Button
          variant="outlined"
          size="small"
          onClick={() => setSelectedRow(params.row)}
        >
          View Details
        </Button>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%", overflowX: "auto" }}>
      <Box sx={{ minWidth: "500px", width: "max-content" }}>
        <DataGrid
          rows={normalizedRows.sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at)
          )}
          columns={columns}
          getRowId={(row) => row.order_id || Math.random()}
          pageSize={10}
          rowsPerPageOptions={[10, 20, 50]}
          disableSelectionOnClick
          autoHeight
          slots={{ toolbar: TableToolbar }}
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f4f4f4",
              fontWeight: "bold",
            },
          }}
        />
      </Box>

      {/* ✅ Details Drawer */}
      <Drawer
        anchor="right"
        open={!!selectedRow}
        onClose={() => setSelectedRow(null)}
        sx={{ "& .MuiDrawer-paper": { width: 400, p: 3 } }}
      >
        {selectedRow ? (
          <>
            <Typography variant="h6" gutterBottom>
              Work Order Details
            </Typography>
            <Typography><b>ID:</b> {selectedRow.order_id}</Typography>
            <Typography><b>Property:</b> {selectedRow.property_id}</Typography>
            <Typography><b>Member:</b> {selectedRow.fullname}</Typography>
            <Typography><b>Address:</b> {selectedRow.full_address}</Typography>
            <Typography><b>Type:</b> {getTypeName?.(selectedRow.workorder_type_id)}</Typography>
            <Typography><b>Status:</b> {selectedRow.status}</Typography>
            <Typography><b>Created:</b> {selectedRow.created_at}</Typography>
            <Typography><b>Resolved:</b> {selectedRow.resolved_at}</Typography>
            <Typography sx={{ mt: 2 }}><b>Details:</b></Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {selectedRow.details || "N/A"}
            </Typography>
            <Typography sx={{ mt: 2 }}><b>Description:</b></Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {selectedRow.description || "N/A"}
            </Typography>
          </>
        ) : null}
      </Drawer>
    </Box>
  );
};

export default WorkOrderTable;
